/**
 * Mux Data helpers.
 *
 * Fetches the public MUX_ENV_KEY from the backend once, caches it in-memory,
 * and exposes a helper to attach mux-embed monitoring to any <video> element
 * (used for LiveKit tiles, since Mux Data has no first-party LiveKit SDK).
 *
 * The Env Key is safe to ship to the browser — it identifies the Mux Data
 * environment, not the API. Missing key ⇒ analytics silently no-op.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import mux from "mux-embed";

let _cached = null;      // { env_key: string | null }
let _inflight = null;    // Promise dedupe

export async function getMuxEnvKey() {
  if (_cached) return _cached.env_key;
  if (_inflight) return _inflight;
  _inflight = api
    .get("/config/mux")
    .then(({ data }) => {
      _cached = { env_key: data?.env_key || null };
      return _cached.env_key;
    })
    .catch(() => {
      _cached = { env_key: null };
      return null;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useMuxEnvKey() {
  const [envKey, setEnvKey] = useState(_cached?.env_key ?? null);
  useEffect(() => {
    let cancelled = false;
    getMuxEnvKey().then((k) => { if (!cancelled) setEnvKey(k); });
    return () => { cancelled = true; };
  }, []);
  return envKey;
}

/**
 * Attach mux-embed monitoring to a native <video> element (for LiveKit).
 * Returns a cleanup function.
 */
export function attachMuxMonitor(videoEl, opts) {
  if (!videoEl || !opts?.envKey) return () => {};
  try {
    mux.monitor(videoEl, {
      debug: false,
      data: {
        env_key: opts.envKey,
        player_name: opts.player_name || "My Panda X Web",
        player_version: opts.player_version || "1.0.0",
        player_init_time: mux.utils.now(),
        video_id: opts.video_id,
        video_title: opts.video_title,
        video_stream_type: opts.video_stream_type || "live",
        video_content_type: opts.video_content_type || "livekit",
        viewer_user_id: opts.viewer_user_id || "anon",
        page_type: opts.page_type || "watchpage",
        sub_property_id: opts.sub_property_id,
      },
    });
    return () => {
      try { mux.destroyMonitor && mux.destroyMonitor(videoEl); } catch { /* noop */ }
    };
  } catch (_e) {
    return () => {};
  }
}
